


import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { ActivatedRoute } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { FilterField, filters, Options, filtersDates, filterUIConfig } from './shared-filter';
import { debounceTime, distinctUntilChanged, firstValueFrom, fromEvent, Subject } from 'rxjs';
import { LocalStorageService } from '../../../core/services/localStorage/local-storage.service';
import { CommonService } from '../../../core/services/common/common.service';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';

import { MatInputModule } from '@angular/material/input';
import { AuthStateService } from '../../../core/services/auth-state/auth-state-service.service';
import { ModelService } from '../../../core/services/model/model.service';
import { BadgeColorPipe } from '../../../core/pipe/badge-color.pipe';
import { FilterTemplateService } from '../../../core/services/filter-template/filter-template.service';
import { FilterTemplate } from '../../../core/interfaces/filter-template';
import { AlertService } from '../../../core/services/alert/alert.service';

@Component({
  selector: 'app-shared-filter',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    TranslateModule,
    ReactiveFormsModule,
    FormsModule,
    MatInputModule,
    MatDatepickerModule,
    MatFormFieldModule,
    BadgeColorPipe
  ],
  templateUrl: './shared-filter.component.html',
  styleUrls: ['./shared-filter.component.css'],
})
export class SharedFilterComponent implements OnInit, OnChanges, OnDestroy {
  @Input() stage_type: string = 'contracts';
  @Input() type: string = 'orders';
  @Input() initialValues: any = {};

  @Output() filterChanged = new EventEmitter<any>();

  @ViewChild('searchBox') searchBox?: ElementRef;
  @ViewChild('requireActionCheckbox') requireActionCheckbox?: ElementRef;

  fields: FilterField[] = [];
  form: FormGroup = new FormGroup({});

  // Para soporte de UI dinámica
  filterFields: any[] = [];
  filterDatesFields: any[] = [];

  searchTerm: string = '';
  searchText: string = '';
  searchSuggestions: any[] = [];

  showFilterModal = false;

  showClear = false;

  ladata: any = [];

  selectedFilters: Set<string> = new Set();

  filterValues: any = {};

  searchParams: any = {};

  isSearchTriggeredByEnter: boolean = false;

  searchTerms: Subject<string> = new Subject<string>();

  // Configuración de elementos UI según el tipo
  showSearch: boolean = true;
  showRequireAction: boolean = false;
  requireActionActive: boolean = false;
  private pendingMultiSelectSearch = false;
  private multiSelectSearchTimer: any = null;

  private filterConfigs = filters;
  private filterDatesConfigs = filtersDates;
  private filterUIConfigs = filterUIConfig;

  // Filter Templates (presets)
  templates: FilterTemplate[] = [];
  showTemplatesDropdown = false;
  showSaveTemplateInput = false;
  newTemplateName = '';
  templateSaving = false;
  selectedTemplateName = '';
  templateSearchTerm = '';

  constructor(private modelService: ModelService, private authStateService: AuthStateService, private commonService: CommonService, private localStorageService: LocalStorageService, private route: ActivatedRoute, private filterTemplateService: FilterTemplateService, private alertService: AlertService) { }

  ngOnChanges(changes: SimpleChanges) {
    // Detectar cambios en el tipo para reorganizar filtros
    if (changes['type'] && !changes['type'].firstChange) {
      this.reorganizeFilters();
      // Always re-fetch to ensure options are loaded for the new type
      this.fetchData();
    }
  }

  ngOnInit() {
    this.fetchData().then(() => {
      this.applyInitialValues();
    });
    this.reorganizeFilters();
    this.loadTemplates();
  }

  /**
   * Pre-aplica filtros recibidos por @Input initialValues.
   * Formato: { 'supplier_reference_id[eq]': 'UUID' }
   * Busca el field por dataKey, selecciona la opción por reference, y dispara search.
   */
  private applyInitialValues(): void {
    if (!this.initialValues || Object.keys(this.initialValues).length === 0) return;

    let applied = false;
    for (const paramKey of Object.keys(this.initialValues)) {
      const value = this.initialValues[paramKey];
      // Extraer dataKey y filtro del paramKey (e.g. 'supplier_reference_id[eq]' → dataKey: 'supplier_reference_id', filtro: 'eq')
      const dataKey = this.limpiarKeyBrackets(paramKey);

      const field = this.filterFields.find((f: any) => f.dataKey === dataKey);
      if (!field || !field.options) continue;

      const option = field.options.find((opt: any) => opt.reference === value || opt.key === value);
      if (option) {
        option.selected = true;
        field.displayText = option.name || option.display || value;
        field.clearable = true;
        this.selectedFilters.add(field.key);
        applied = true;
      }
    }

    if (applied) {
      this.search();
    }
  }

  ngOnDestroy() {
    if (this.multiSelectSearchTimer) {
      clearTimeout(this.multiSelectSearchTimer);
    }
  }

  get selectedStages(): any[] {
    const stageField = this.filterFields.find((f: any) => f.dataKey === 'stage');
    return stageField ? stageField.options.filter((opt: any) => opt.selected) : [];
  }

  private reorganizeFilters() {
    // Configurar elementos UI según el tipo
    const uiConfig = this.filterUIConfigs[this.type] || this.filterUIConfigs['default'];
    this.showSearch = uiConfig.showSearch;
    this.showRequireAction = uiConfig.showRequireAction;

    this.filterFields = this.filterConfigs[this.type] ? this.checkHiddenFor(this.filterConfigs[this.type]) : [];
    this.filterDatesFields = this.filterDatesConfigs[this.type] ? this.checkHiddenFor(this.filterDatesConfigs[this.type]) : [];

    // Limpiar las options de filterFields
    this.filterFields.forEach(field => {
      if (field.options) {
        field.options = [];
      }
      // Inicializar searchTerm y filteredOptions
      field.searchTerm = '';
      field.filteredOptions = [];
    });

    // Limpiar filtros seleccionados al cambiar de tipo
    this.selectedFilters.clear();
    this.searchParams = {};
    this.clearFilter();
  }

  ngAfterViewInit() {
    // Detectar cambios en el input con debounce y evitar duplicados
    this.searchTerms
      .pipe(debounceTime(1500), distinctUntilChanged())
      .subscribe(() => {
        if (!this.isSearchTriggeredByEnter) {
          if (this.searchText.trim() !== '') {
            this.search();
          }
        }
        this.isSearchTriggeredByEnter = false; // Resetear bandera después de debounce
      });

    // Detectar eventos de teclado para manejar Enter y limpiar (solo si showSearch está activo)
    if (this.showSearch && this.searchBox) {
      fromEvent<KeyboardEvent>(this.searchBox.nativeElement, 'keyup')
        .subscribe((event: KeyboardEvent) => {
          const term = (event.target as HTMLInputElement).value;
          this.searchText = term;
          this.searchTerms.next(term);
          if (event.key === 'Enter') {
            this.isSearchTriggeredByEnter = true;
            this.search();
          }
          // Detectar si el input fue limpiado (vacío)
          if (term === '') {
            this.search();
          }
        });
    }
  }

  async preCargarFiltros(): Promise<void> {
    
    this.filterValues = [];
    if (!this.searchParams.hasOwnProperty("require_action")) {
       if (this.showRequireAction && this.requireActionCheckbox && this.localStorageService.getItem('account_name') != 'idoneo.com') {
        this.requireActionCheckbox.nativeElement.checked = true;
        this.selectedFilters.add('require_action');
      }
    }

    // Primero buscar si hay una marca seleccionada y cargar sus modelos
    let brandReference: string | null = null;
    for (const i in this.searchParams) {
      if (this.searchParams.hasOwnProperty(i)) {
        let key = this.limpiarKeyBrackets(i);
        if (key === 'vehicle_brand_id' || key === 'vehicle_brand_reference_id') {
          brandReference = this.searchParams[i];
          break;
        }
      }
    }

    // Si hay una marca seleccionada, cargar los modelos antes de continuar
    if (brandReference) {
      await this.searchModelForBrand(brandReference);
    }

    for (const i in this.searchParams) {

      if (this.searchParams.hasOwnProperty(i)) {
        let key = this.limpiarKeyBrackets(i);
        let value = this.searchParams[i];
        if (key === 'require_action' && value === 'false') {
          if (this.showRequireAction && this.requireActionCheckbox) {
            this.requireActionCheckbox.nativeElement.checked = false;
            this.selectedFilters.delete('require_action');
          }
        }
        if (key === 'require_action' && value === 'true') {
          if (this.showRequireAction && this.requireActionCheckbox) {
            this.requireActionCheckbox.nativeElement.checked = true;
            this.selectedFilters.add('require_action');
          }
        }
        this.filterFields.forEach((field: any) => {
          if (field.dataKey === key) {

            // Para filtros 'like' (como vehicle_model), el valor es el nombre con %
            if (field.filtro === 'like') {
              // Remover los % del valor guardado
              let cleanValue = typeof value === 'string' ? value.replace(/%/g, '') : value;
              field.options.forEach((option: Options) => {
                if (option.name === cleanValue) {
                  option.selected = true;
                }
              });
            } else {
              // Detectar si es filtro tipo 'in' (array) o 'eq'
              let valuesArray: string[] = [];
              if (Array.isArray(value)) {
                valuesArray = value;
              } else if (typeof value === 'string' && value.includes(',')) {
                valuesArray = value.split(',');
              } else if (typeof value === 'string') {
                valuesArray = [value];
              } else {
                valuesArray = [value];

              }
              field.options.forEach((option: Options) => {
                if (valuesArray.includes(option.reference)) {
                  option.selected = true;
                }
              });
            }
            // Actualizar displayText y clearable solo si hay seleccionados
            const selectedOptions = field.options.filter((opt: any) => opt.selected);
            if (selectedOptions.length > 0) {
              field.displayText = selectedOptions.map((opt: any) => opt.name || opt.display).join(', ');
              field.clearable = true;
              this.selectedFilters.add(field.key);
            }
          }
        });

        this.filterDatesFields.forEach((field: any) => {
          if (field.dataKey === key) {
            let dateValues = Array.isArray(value) ? value : value.split(',');
            this.filterValues[field.dataKey] = [dateValues[0] || null, dateValues[1] || null];
            field.clearable = true;
            this.selectedFilters.add(field.key);
            field.displayText = dateValues.filter(Boolean).join(' - ');
          }
        });
      }
    }
    this.updateButtonLabel();
    this.search();
  }

  limpiarKeyBrackets(str: string): string {
    const idx = str.indexOf('[');
    return idx !== -1 ? str.substring(0, idx) : str;
  }

  fetchData(): Promise<void> {
    return new Promise((resolve, reject) => {
      let params = {
        stageType: this.stage_type == '' ? 'all' : this.stage_type,
        deliveryStatusType: 'all',
        telemarketingActionType: 'all'

      };
      this.commonService.getFilterData(params).subscribe({
        next: async (data) => {
          this.ladata = data;
          this.fillAllFilters(data);
          await this.getFilterConfigInLocalStorage();
          resolve();
        },
        error: (error) => {
          reject(error);
        },
      });
    });
  }
  fillAllFilters(data: any): void {
    this.fillFilter('broker_agent_id', data.data.agents);
    this.fillFilter('broker_sales_agent_id', data.data.agents);
    this.fillFilter('supplier_agent_id', data.data.agents);
    this.fillFilter('broker_reference_id', data.data.accounts.broker);
    this.fillFilter('vendor_reference_id', data.data.accounts.vendor);
    this.fillFilter('supplier_reference_id', data.data.accounts.supplier);
    this.fillFilter('workshop_reference_id', data.data.accounts.workshop);
    this.fillFilter('vehicle_brand_reference_id', data.data.brands);
    this.fillFilter('vehicle_brand_id', data.data.brands);
    this.fillFilter('order_type', data.data.order_types);
    this.fillFilter('campaign_reference_id', data.data.campaigns);
    this.fillFilter('campaign_stock_reference_id', data.data.campaign_stock);
    this.fillFilter('delivery_status', data.data.delivery_statuses_map);
    this.fillFilter('tag_reference_id', data.data.tags);
    this.fillFilter('telemarketing_status', data.data.telemarketing_statuses);
    this.fillFilter('vehicle_status', [{ reference: 'new', name: 'Nuevo' }, { reference: 'used', name: 'Usado' }]);
    this.fillFilter('vehicle_registered', [{ reference: '1', name: 'Registrado' }, { reference: '0', name: 'No registrado' }]);
    this.fillFilter('entity', [{ reference: 'orders', name: 'Pedidos' }, { reference: 'campaigns', name: 'Campañas' }]); // Se llenará dinámicamente al seleccionar marca
    this.fillFilter('telemarketing_action_status', data.data.telemarketing_action_statuses_map);

    // Para campaigns y stocks, usar stages específicas
    if (this.type === 'campaigns' || this.type === 'stocks' || this.type === 'expanded') {
      this.fillFilter('stage', [
        { key: 'enabled', name: 'Habilitada' },
        { key: 'draft', name: 'Borrador' },
        { key: 'cancelled', name: 'Cancelada' },
        { key: 'finished', name: 'Finalizada' },
        { key: 'paused', name: 'Pausada' }
      ]);
    } else {
      this.fillFilter('stage', data.data.order_stages_map);
    }
    this.fillFilter('client_type', [
      { name: 'Empresa', reference: 'organization' },
      { name: 'Autónomo', reference: 'self-employed' },
      { name: 'Particular', reference: 'individual' }
    ])

    this.fillFilter('supplier_type', [
      { name: 'Prescripción broker', reference: 'referral_broker' },
      { name: 'Prescripción devolución', reference: 'referral_vendor' }
    ]);

    // Llenar selects de rango de etapa (ahora están en filterFields)
    if (data.data.order_stages_map) {
      this.fillFilter('stage_from', data.data.order_stages_map);
      this.fillFilter('stage_to', data.data.order_stages_map);
    }
  }

  fillFilter(term: string, data: any): void {
    this.filterFields.forEach((field: any) => {
      if (field.dataKey == term && Array.isArray(field.options)) {
        switch (term) {
          case 'broker_agent_id':
          case 'broker_sales_agent_id':
          case 'supplier_agent_id':
            data.forEach((item: any) => {
              field.options.push({
                reference: item.reference,
                name: item.name + " " + item.lastname,
                selected: false
              });
            });
            break;
          case 'broker_reference_id':
          case 'vendor_reference_id':
          case 'supplier_reference_id':
          case 'workshop_reference_id':
          case 'campaign_reference_id':
          case 'tag_reference_id':
          case 'vehicle_model_reference_id':
          case 'vehicle_model_id':
          case 'vehicle_model':
            data.forEach((item: any) => {
              field.options.push({
                reference: item.reference,
                name: item.name,
                selected: false
              });
            });
            break;
          case 'campaign_stock_reference_id':
            data.forEach((item: any) => {
              field.options.push({
                reference: item.campaign_stock_reference_id,
                name: item.campaign_stock_reference,
                selected: false
              });
            });
            break;
          case 'telemarketing_status':
          case 'telemarketing_action_status':

          case 'stage':
          case 'stage_from':
          case 'stage_to':
          case 'delivery_status':
            data.forEach((item: any) => {
              field.options.push({
                reference: item.key,
                name: item.name,
                selected: false
              });
            });
            break;
          case 'vehicle_brand_reference_id':
          case 'vehicle_brand_id':
          case 'supplier_type':
          case 'vehicle_status':
          case 'vehicle_registered':
          case 'client_type':
          case 'entity':
            field.options.push(...data);
            break;
          default:

            break;
        }
        // Inicializar filteredOptions con todas las opciones
        field.filteredOptions = [...field.options];
      }
    });
  }

  fillFilterDates(dataKey: string, data: any): void {
    this.filterDatesFields.forEach((field: any) => {
      if (field.dataKey === dataKey && field.type === 'select' && Array.isArray(field.options)) {
        field.options = [];
        data.forEach((item: any) => {
          field.options.push({
            reference: item.key,
            name: item.name,
            selected: false,
          });
        });
        field.filteredOptions = [...field.options];
      }
    });
  }

  checkHiddenFor(fields: any[]): any[] {
    let role = '';
    let accountType = '';

    role = this.authStateService.getRole() || '';
    accountType = this.authStateService.getAccountType() || '';

    // Filtrar campos
    return fields.filter(field => {
      const hasRole = field.hiddenFor && Array.isArray(field.hiddenFor.role);
      const hasAccountType = field.hiddenFor && Array.isArray(field.hiddenFor.accountType);
      const roleMatch = hasRole && field.hiddenFor.role.includes(role);
      const accountTypeMatch = hasAccountType && field.hiddenFor.accountType.includes(accountType);

      // If both role and accountType are present, both must match to hide
      if (hasRole && hasAccountType) {
        if (roleMatch && accountTypeMatch) {
          return false;
        }
      } else if (hasRole && roleMatch) {
        return false;
      } else if (hasAccountType && accountTypeMatch) {
        return false;
      }
      return true;
    });
  }

  //manejo interno de los filtros
  onFocus(key: string) {
    const field = this.filterFields.find(f => f.key === key);
    if (field) {
      field.optionsVisible = true;
      // Inicializar filteredOptions si no existe
      if (!field.filteredOptions) {
        field.filteredOptions = [...field.options];
      }
    }
  }

  onInputChange(field: any, event: any) {
    const searchTerm = event.target.value.toLowerCase();
    field.searchTerm = searchTerm;

    // Si hay un término de búsqueda, filtrar las opciones
    if (searchTerm) {
      field.filteredOptions = field.options.filter((option: any) =>
        (option.name || option.display || '').toLowerCase().includes(searchTerm)
      );
    } else {
      // Si no hay término, mostrar todas las opciones
      field.filteredOptions = [...field.options];
    }

    // Mantener el dropdown abierto mientras se escribe
    field.optionsVisible = true;
  }

  hideOptions() {
    this.filterFields.forEach(f => {
      f.optionsVisible = false;
      // Limpiar el término de búsqueda cuando se cierra
      f.searchTerm = '';
      f.filteredOptions = [...f.options];
    });
    // Cancelar timer pendiente y ejecutar búsqueda inmediatamente
    if (this.multiSelectSearchTimer) {
      clearTimeout(this.multiSelectSearchTimer);
      this.multiSelectSearchTimer = null;
    }
    if (this.pendingMultiSelectSearch) {
      this.pendingMultiSelectSearch = false;
      this.search();
    }
  }

  clearAllFilter(event: Event): void {
    event.stopPropagation();
    this.clearFilter();
    this.updateButtonLabel();
    this.hideOptions();
    this.search();
  }

  clearFilter(): void {
    this.localStorageService.removeItem('sortColumn');
    this.localStorageService.removeItem('sortDirection');
    this.selectedFilters.clear();
    this.searchText = '';
    this.filterFields.forEach(field => {
      field.displayText = '';
      field.searchTerm = '';
      field.clearable = false;
      field.filteredOptions = [...field.options];
      field.options?.forEach((option: any) => {
        option.selected = false;
      });
    });
    this.filterDatesFields.forEach(field => {
      field.displayText = '';
      field.searchTerm = '';
      field.clearable = false;
      field.options?.forEach((option: any) => {
        option.selected = false;
      });
    });
    this.filterValues = {};
    if (this.showRequireAction && this.requireActionCheckbox) {
      this.requireActionCheckbox.nativeElement.checked = false;
    }
    this.showClear = false;
    this.searchParams = {};
    this.pendingMultiSelectSearch = false;

  }
  updateButtonLabel() {
    const filterCount = this.selectedFilters.size;
    this.showClear = filterCount > 0;
  }

  clearSelectedOption(field: any) {
    field.searchTerm = '';
    field.filteredOptions = [...field.options];
    this.selectedFilters.delete(field.key);
    field.options.forEach((option: any) => {
      option.selected = false;
    });
    this.selectedFilters.delete(field.key);

    // Si se limpia una marca, también limpiar los modelos asociados
    if (field.dataKey === 'vehicle_brand_reference_id' || field.dataKey === 'vehicle_brand_id') {
      this.filterFields.forEach((f: any) => {
        if (f.dataKey === 'vehicle_brand_reference_id' || f.dataKey === 'vehicle_brand_id') {
          f.clearable = false;
          f.displayText = '';
          f.searchTerm = '';
          f.filteredOlearable = false;
          f.displayText = '';
          this.selectedFilters.delete(f.key);
        }
      });


      this.filterFields.forEach((f: any) => {
        if (f.dataKey === 'vehicle_model_reference_id' || f.dataKey === 'vehicle_model' || f.dataKey === 'vehicle_model_id') {

          f.clearable = false;
          f.displayText = '';
          f.searchTerm = '';
          f.filteredOlearable = false;
          f.displayText = '';
          f.options = [];
          f.filteredOptions = [];

          this.selectedFilters.delete(f.key);
        }
      });
    }else{
      this.filterFields.forEach((f: any) => {
        if (f.dataKey === field.dataKey ) {
          f.clearable = false;
          f.displayText = '';
          f.searchTerm = '';
          f.filteredOlearable = false;
          f.displayText = '';
          this.selectedFilters.delete(f.key);
        }
      });
    }

    // Limpieza pareada: si el campo tiene pairedWith, limpiar también el par
    if (field.pairedWith) {
      const pairField = this.filterFields.find((f: any) => f.key === field.pairedWith);
      if (pairField) {
        pairField.options?.forEach((opt: any) => opt.selected = false);
        pairField.displayText = '';
        pairField.clearable = false;
        pairField.searchTerm = '';
        this.selectedFilters.delete(pairField.key);
      }
    }

    this.updateButtonLabel();
    this.search();
  }

  onDateChange(event: any, dateType: 'startDate' | 'endDate', dataKey?: string): void {
    if (!dataKey) return;
    if (!this.filterValues[dataKey]) {
      this.filterValues[dataKey] = [null, null];
    }
    const selectedDate = event.value;
    let formattedDate = null;
    if (selectedDate) {
      if (selectedDate.format) {
        // Moment object (MomentDateAdapter) — usar format() que respeta su timezone
        formattedDate = selectedDate.format('YYYY-MM-DD');
      } else if (selectedDate instanceof Date) {
        const year = selectedDate.getFullYear();
        const month = (selectedDate.getMonth() + 1).toString().padStart(2, '0');
        const day = selectedDate.getDate().toString().padStart(2, '0');
        formattedDate = `${year}-${month}-${day}`;
      } else if (typeof selectedDate === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(selectedDate)) {
        formattedDate = selectedDate;
      } else {
        const d = new Date(selectedDate);
        if (!isNaN(d.getTime())) {
          const year = d.getFullYear();
          const month = (d.getMonth() + 1).toString().padStart(2, '0');
          const day = d.getDate().toString().padStart(2, '0');
          formattedDate = `${year}-${month}-${day}`;
        } else {
          formattedDate = String(selectedDate);
        }
      }
    }
    if (dateType === 'startDate') {
      this.filterValues[dataKey][0] = formattedDate;
    } else if (dateType === 'endDate') {
      this.filterValues[dataKey][1] = formattedDate;
    }

    // Si ambos valores están definidos, dispara búsqueda
    if (this.filterValues[dataKey][0] && this.filterValues[dataKey][1]) {
      this.selectedFilters.add(dataKey);
      this.search();
    }
  }

  clearDateFilter(dataKey: string, dateType: 'startDate' | 'endDate'): void {
    if (!this.filterValues[dataKey]) {
      return;
    }

    // Limpiar la fecha específica
    if (dateType === 'startDate') {
      this.filterValues[dataKey][0] = null;
    } else if (dateType === 'endDate') {
      this.filterValues[dataKey][1] = null;
    }

    // Si ambas fechas están vacías, eliminar el filtro
    if (!this.filterValues[dataKey][0] && !this.filterValues[dataKey][1]) {
      this.selectedFilters.delete(dataKey);
    }

    // Actualizar label y ejecutar búsqueda
    this.updateButtonLabel();
    this.search();
  }

  onSelectOption(options: any, field: any, inputRef: any, event?: MouseEvent) {

    if (options.type == 'checkbox') {
      if (field.key == 'require_action') {
        if (this.showRequireAction && this.requireActionCheckbox && this.requireActionCheckbox.nativeElement.checked) {

          this.selectedFilters.add(field.key);
        } else {

          this.selectedFilters.delete(field.key);
        }
      }
    } else {
      if (field.filtro == 'eq' || field.filtro == 'raw') {
        field.displayText = options.name || options.display;
        field.options.forEach((opt: any) => {
          opt.selected = false;
        });
        options.selected = true;
      } else if (field.filtro == 'in') {
        // Permitir multiselección
        options.selected = !options.selected;
        // Actualizar displayText con los seleccionados
        const selectedOptions = field.options.filter((opt: any) => opt.selected);
        field.displayText = selectedOptions.map((opt: any) => opt.name || opt.display).join(', ');
      } else {
        options.selected = true;
        field.displayText = field.displayText !== '' ? field.displayText + "," + (options.name || options.display) : options.name || options.display;
      }

      field.clearable = true;
      this.selectedFilters.add(field.key);
    }

    // Cargar modelos DESPUÉS del toggle para que todas las marcas seleccionadas estén marcadas
    if (field.dataKey == 'vehicle_brand_reference_id' || field.dataKey == 'vehicle_brand_id') {
      this.searchModelForBrand(options.reference);
    }

    if (field.filtro === 'in') {
      // Mantener dropdown abierto: preventDefault evita que el browser mueva el foco
      if (event) event.preventDefault();
      this.pendingMultiSelectSearch = true;
      this.updateButtonLabel();
      // Auto-search 3s después del último check
      if (this.multiSelectSearchTimer) clearTimeout(this.multiSelectSearchTimer);
      this.multiSelectSearchTimer = setTimeout(() => {
        if (this.pendingMultiSelectSearch) {
          this.pendingMultiSelectSearch = false;
          this.search();
        }
      }, 3000);
    } else {
      if (inputRef && inputRef.blur) inputRef.blur();

      // Filtros pareados: no disparar search hasta que ambos tengan valor
      if (field.pairedWith) {
        const pairField = this.filterFields.find((f: any) => f.key === field.pairedWith);
        const pairHasValue = pairField?.options?.some((opt: any) => opt.selected);
        if (!pairHasValue) return;
      }

      this.search();
    }
  }

  /********  MODAL FILTER **********/
  async searchModelForBrand(value: any) {
    // Buscar el campo de marca para obtener TODAS las seleccionadas
    const brandField = this.filterFields.find((f: any) =>
      f.dataKey === 'vehicle_brand_reference_id' || f.dataKey === 'vehicle_brand_id'
    );

    const selectedBrandRefs: string[] = [];
    if (brandField && brandField.options) {
      brandField.options
        .filter((opt: any) => opt.selected)
        .forEach((opt: any) => selectedBrandRefs.push(opt.reference));
    }

    // Si no hay marcas seleccionadas por toggle (primera selección), usar el value directo
    if (selectedBrandRefs.length === 0 && value) {
      if (typeof value === 'string') {
        selectedBrandRefs.push(value);
      } else if (Array.isArray(value)) {
        selectedBrandRefs.push(...value);
      }
    }

    if (selectedBrandRefs.length === 0) return;

    // Limpiar opciones previas de modelos
    this.filterFields.forEach((field: any) => {
      if (field.dataKey === 'vehicle_model_reference_id' || field.dataKey === 'vehicle_model' || field.dataKey === 'vehicle_model_id') {
        field.options = [];
        field.filteredOptions = [];
        field.displayText = '';
        field.searchTerm = '';
        field.clearable = false;
      }
    });

    // Cargar modelos de TODAS las marcas seleccionadas
    for (const brandRef of selectedBrandRefs) {
      try {
        const data = await firstValueFrom(this.modelService.getDataByBrand(brandRef));
        // Acumular modelos (fillFilter hace push, no reemplaza)
        this.fillFilter('vehicle_model_reference_id', data.data);
        this.fillFilter('vehicle_model', data.data);
        this.fillFilter('vehicle_model_id', data.data);
      } catch (error) {
        console.error('Error loading models for brand:', brandRef, error);
      }
    }
  }

  objectKeys(obj: any): string[] {
    return Object.keys(obj);
  }

  keyup(event: KeyboardEvent) {
    this.filterValues.search = (event.target as HTMLInputElement).value;
    this.searchTerms.next(this.filterValues.search);
  }

  openFilterModal() {
    this.showFilterModal = true;
  }

  closeFilterModal() {
    this.showFilterModal = false;
    this.hideOptions();
  }

  get filterLabel() {
    let filterCount = this.selectedFilters.size;
    // No contar require_action en el total
    if (this.selectedFilters.has('require_action')) {
      filterCount--;
    }
    return filterCount > 0 ? `Filtros Totales (${filterCount})` : '';
  }

  get filterButtonLabel() {
    // Contar todos los filtros seleccionados excepto require_action
    let filterCount = this.selectedFilters.size;
    if (this.selectedFilters.has('require_action')) {
      filterCount--;
    }
    return filterCount > 0 ? `Filtrar (${filterCount})` : 'Filtrar';
  }

  search(): void {

    // Permitir multiselección para filtros tipo 'in'
    this.searchParams = {};
    if (this.filterValues.search) {
      this.searchParams['search'] = `${this.filterValues.search}`;
    }
    this.selectedFilters.forEach((key) => {
      if (key === 'require_action') {
        if (this.showRequireAction && this.requireActionCheckbox && this.requireActionCheckbox.nativeElement.checked) {
          this.searchParams['require_action'] = 'true';
        } else {
          this.searchParams['require_action'] = 'false';
        }
      } else {
        const field = this.filterFields.find(f => f.key === key);
        if (field) {
          if (field.filtro == 'eq') {

            const selectedOptions = field.options
              .filter((opt: any) => opt.selected)
              .map((opt: any) => opt.reference || opt.key);
            this.searchParams[`${field.dataKey}[${field.filtro}]`] = selectedOptions[0];
          } else if (field.filtro == 'in') {

            const selectedOptions = field.options
              .filter((opt: any) => opt.selected)
              .map((opt: any) => opt.reference || opt.key);
            this.searchParams[`${field.dataKey}[${field.filtro}][]`] = selectedOptions;
          } else if (field.filtro == 'like') {

            // Para filtros 'like', usar el nombre en lugar de reference (ej: vehicle_model en campaigns)
            const selectedOptions = field.options
              .filter((opt: any) => opt.selected)
              .map((opt: any) => opt.name);
            if (selectedOptions.length > 0) {
              this.searchParams[`${field.dataKey}[${field.filtro}]`] = `%${selectedOptions[0]}%`;
            }
          } else if (field.filtro == 'raw') {
            const selectedOptions = field.options
              .filter((opt: any) => opt.selected)
              .map((opt: any) => opt.reference || opt.key);
            if (selectedOptions.length > 0) {
              this.searchParams[field.dataKey!] = selectedOptions[0];
            }
          } else if (field.type === 'select') {
            const selectedOptions = field.options
              .filter((opt: any) => opt.selected)
              .map((opt: any) => opt.reference || opt.key);
            if (selectedOptions.length > 0) {
              this.searchParams[`${field.dataKey}[${field.filtro}]`] = selectedOptions.join(',');
            }
          }
        }
        const fieldDate = this.filterDatesFields.find(f => f.key === key);
        if (fieldDate) {
          if (fieldDate.filtro == 'between') {
            this.searchParams[`${fieldDate.dataKey}[${fieldDate.filtro}][]`] = this.filterValues[fieldDate.dataKey] ? this.filterValues[fieldDate.dataKey] : null;
          } else if (fieldDate.filtro == 'raw_date') {
            const dateValues = this.filterValues[fieldDate.dataKey!];
            if (dateValues && dateValues[0]) this.searchParams['date_from'] = dateValues[0];
            if (dateValues && dateValues[1]) this.searchParams['date_to'] = dateValues[1];
          } else if (fieldDate.filtro == 'raw' && fieldDate.type == 'select') {
            const selectedOptions = fieldDate.options
              ?.filter((opt: any) => opt.selected)
              .map((opt: any) => opt.reference || opt.key) || [];
            if (selectedOptions.length > 0) {
              this.searchParams[fieldDate.dataKey!] = selectedOptions[0];
            }
          }
        }
      }
    });

    // Si hay date_from/date_to, combinar con el filtro "Etapa" de arriba como stages[]
    if (this.searchParams['date_from'] || this.searchParams['date_to']) {
      const stageField = this.filterFields.find(f => f.dataKey === 'stage');
      if (stageField) {
        const selectedStages = stageField.options
          ?.filter((opt: any) => opt.selected)
          .map((opt: any) => opt.reference || opt.key) || [];
        if (selectedStages.length > 0) {
          this.searchParams['stages[]'] = selectedStages;
          // Remover el filtro stage[in][] para no duplicar
          delete this.searchParams['stage[in][]'];
        }
      }
    }

    let store_searchParams = this.searchParams;
    this.storeFilterConfigInLocalStorage(store_searchParams);
    this.updateButtonLabel();
    this.emitEvent();
  }

  checkFilterConfigInLocalStorage() {


    switch (this.type) {
      case 'orders':
        if (this.localStorageService.getItem('searchParams')) {
          if (this.localStorageService.getItem('searchParams') !== '{}') {
            return true;
          }
        }
        break;
      case 'campaigns':
        if (this.localStorageService.getItem('searchParamsCampaign')) {
          if (this.localStorageService.getItem('searchParamsCampaign') !== '{}') {
            return true;
          }
        }
        break;
      case 'stocks':
        if (this.localStorageService.getItem('searchParamsStockCampaign')) {
          if (this.localStorageService.getItem('searchParamsStockCampaign') !== '{}') {
            return true;
          }
        }
        break;
      case 'contracts':
        if (this.localStorageService.getItem('searchParamsContracts')) {
          return true;
        }
        break;
      default:

        return false;
        break;
    }
    return false;
  }

  async getFilterConfigInLocalStorage(): Promise<void> {
    const storageKey = this.getStorageKeyForType(this.type);
     if (this.localStorageService.getItem(storageKey || '')) {
      this.searchParams = JSON.parse(this.localStorageService.getItem(storageKey || '') || '{}');
        }
     this.searchParams = JSON.parse(this.localStorageService.getItem(storageKey || '') || '{}');
     
     if (this.searchParams && Object.keys(this.searchParams).length > 0) {
      await this.preCargarFiltros();
    }
    

  }

  storeFilterConfigInLocalStorage(params: any): void {
    const storageKey = this.getStorageKeyForType(this.type);
    if (storageKey) {
      this.localStorageService.setItem(storageKey, JSON.stringify(params).replace(/\%/g, ""));
    }
  }

  public setFilterForType(filterType: string, params: any): void {
    const storageKey = this.getStorageKeyForType(filterType);
    if (storageKey) {
      this.localStorageService.setItem(storageKey, JSON.stringify(params).replace(/\%/g, ""));
      this.searchParams = params;
      this.emitEvent();
    }
  }


  private getStorageKeyForType(filterType: string): string | null {
    switch (filterType) {
      case 'orders':
        return 'searchParams';
      case 'campaigns':
        return 'searchParamsCampaign';
      case 'stocks':
        return 'searchParamsStockCampaign';
      case 'expanded':
        return 'searchParamsExpandedCampaign';
      case 'automation':
        return 'searchParamsAutomation';
      case 'contracts':
        return 'searchParamsContracts';
      case 'clients':
        return 'searchParamsClients';
      default:
        return null;
    }
  }

  emitEvent(): void {
    this.filterChanged.emit(this.searchParams);

  }

  // ===== Filter Templates (Presets) =====

  loadTemplates(): void {
    const entity = (this.type === 'stocks' || this.type === 'expanded') ? 'orders' : this.type;
    this.filterTemplateService.list(entity).subscribe({
      next: (res: any) => {
        this.templates = res.data || [];
      },
      error: () => {
        this.templates = [];
      }
    });
  }

  toggleTemplatesDropdown(): void {
    this.showTemplatesDropdown = !this.showTemplatesDropdown;
    this.showSaveTemplateInput = false;
    this.newTemplateName = '';
  }

  openSaveTemplateInput(): void {
    this.showSaveTemplateInput = true;
    this.newTemplateName = '';
  }

  saveTemplate(): void {
    const name = this.newTemplateName.trim();
    if (!name || this.templateSaving) return;

    this.templateSaving = true;
    const entity = (this.type === 'stocks' || this.type === 'expanded') ? 'orders' : this.type;
    const filters = { ...this.searchParams };

    this.filterTemplateService.create(entity, name, filters).subscribe({
      next: (res: any) => {
        this.templates.push(res.data);
        this.showSaveTemplateInput = false;
        this.newTemplateName = '';
        this.templateSaving = false;
      },
      error: () => {
        this.templateSaving = false;
      }
    });
  }

  applyTemplate(template: FilterTemplate): void {
    this.clearFilter();
    this.searchParams = { ...template.filters };
    this.selectedTemplateName = template.name;
    this.storeFilterConfigInLocalStorage(this.searchParams);
    this.preCargarFiltros();
    this.showTemplatesDropdown = false;
  }

  clearTemplateSelection(event: Event): void {
    event.stopPropagation();
    this.selectedTemplateName = '';
    this.clearFilter();
    this.updateButtonLabel();
    this.hideOptions();
    this.search();
  }

  closeTemplatesDropdownDelayed(): void {
    setTimeout(() => {
      this.showTemplatesDropdown = false;
    }, 200);
  }

  async deleteTemplate(event: Event, template: FilterTemplate): Promise<void> {
    event.stopPropagation();
    event.preventDefault();
    this.showTemplatesDropdown = false;
    const result = await this.alertService.confirmation2(
      'filter_templates.confirm_delete',
      'swal_messages.confirm',
      'swal_messages.confirm',
      'swal_messages.cancel'
    );
    if (!result.isConfirmed) return;
    this.filterTemplateService.delete(template.reference).subscribe({
      next: () => {
        this.templates = this.templates.filter(t => t.reference !== template.reference);
      }
    });
  }

  closeTemplatesDropdown(): void {
    this.showTemplatesDropdown = false;
    this.showSaveTemplateInput = false;
  }

}
